import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/ja'
import { mapState, mapGetters } from 'vuex';
import {
  BIZMATES_TIMEZONE_STUDENT,
  ZIPAN_TIMEZONE_ADMIN,
} from '@/utils/constants';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Mixing based from [MBTI_frontend] library to convert datetime appropriately
 * according to user's local timezone.
 *
 * Usage:
 * 1. Import the mixin in your component:
 *    import userLocalTimezone from '@/mixins/userLocalTimezone';
 * 2. Add it to the mixins array in your component:
 *  mixins: [userLocalTimezone],
 * 3. Use the provided methods to convert datetime:
 *   this.toLocal(yourDateTime);
 */
export default {
  computed: {
    ...mapState(['actualAcctType']),
    ...mapState('userOtherSetting', ['userTimezone']),
    ...mapGetters(['isZipanService']),
    ...mapGetters('userOtherSetting', ['trainerTimeZone'])
  },
  methods: {
    /**
     * Convert given datetime (in either Unix timestamp in seconds or
     * string format) to the user's local time zone and return an object
     * containing the time, date, display date (in MM/DD format), and
     * day of the week in Japanese/English.
     *
     * @param {Number|String} paramDateTime - datetime to be converted
     * @returns {Object} - an object containing the time, date, display date,
     *   and day of the week in Japanese/English, as well as the raw dateObject
     */
    toLocal(paramDateTime) {
      let bizmatesTimezone = this.actualAcctType === 's' ? BIZMATES_TIMEZONE_STUDENT : this.trainerTimeZone;
      let userTimezone = this.actualAcctType === 's' ? this.userTimezone : this.trainerTimeZone;
      let locale = this.actualAcctType === 's' ? 'ja' : 'en';

      if (this.isZipanService) {
        // in Zipan, it is considered, that Trainers use JP LANG and TIMEZONE
        bizmatesTimezone = this.trainerTimeZone;
        userTimezone = this.actualAcctType === 's' ? this.userTimezone : (this.actualAcctType === 't' ? this.trainerTimeZone : ZIPAN_TIMEZONE_ADMIN);
        locale = this.actualAcctType === 't' ? 'ja' : 'en';
      }

      return this.getDateTimeInUserTimezone(paramDateTime, bizmatesTimezone, userTimezone, locale);
    },

    /**
     * Fetch correct date time via user timezone details
     *
     * @param {Number|String} paramDateTime - datetime to be converted
     * @param {String} bizmatesTimezone - Base timezone, @todo check if still important
     * @param {String} userTimezone - User selected timezone
     * @param {String} locale - Frontend locale/language for UI display
     * @returns {Object} - an object containing the time, date, display date,
     *   and day of the week in Japanese/English, as well as the raw dateObject
     */
    getDateTimeInUserTimezone(paramDateTime, bizmatesTimezone, userTimezone, locale) {
      let datetime;
      // If passed value is a number (assume it's a Unix timestamp in seconds)
      if (typeof paramDateTime === 'number') {
        datetime = dayjs.unix(paramDateTime).tz(bizmatesTimezone);
      } else {
        datetime = dayjs.utc(paramDateTime).tz(bizmatesTimezone);
      }

      const currentLocalDateTime = datetime.tz(userTimezone);
      const dayOfWeek = currentLocalDateTime.locale(locale).format('ddd');

      return {
        time: currentLocalDateTime.format('HH:mm'),
        date: currentLocalDateTime.format('YYYY-MM-DD'),
        displayDate: currentLocalDateTime.format('MM/DD'),
        dow: dayOfWeek,
        dateObject: currentLocalDateTime,
        localeTime: currentLocalDateTime.format(locale == 'ja' ? "H:mm" : "h:mm A"),
        formattedTime: currentLocalDateTime.format("hh:mm A")
      };
    },
  }
};
